import { useState, type FormEvent } from 'react';
import { Check } from 'lucide-react';

import type { Tag, TagCategoria } from '@/api/tags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { TagChip } from '@/components/ui/tag-chip';
import { CORES_TAG, corDoTexto } from '@/lib/tags/cores';
import { cn } from '@/lib/utils';

/** Limite do nome (PLANO_TAGS D9), o mesmo do backend. */
const NOME_MAX = 50;
// O Select do Radix não aceita valor vazio.
const SEM_CATEGORIA = 'sem';
const PESSOAL = 'pessoal';

export interface Escopo {
  /** O escritório; nulo = pessoal. */
  id: string | null;
  nome: string;
  /** A equipe, no escritório `id` (A12). */
  equipeId?: string | null;
}

export interface TagFormValores {
  nome: string;
  cor: string;
  categoriaId: string | null;
  escritorioId: string | null;
  equipeId: string | null;
}

interface TagFormProps {
  inicial?: Tag;
  escritorioId: string | null;
  equipeId?: string | null;
  categoriaId: string | null;
  escopos: Escopo[];
  categorias: TagCategoria[];
  salvando: boolean;
  onSalvar: (valores: TagFormValores) => Promise<void>;
  onCancelar: () => void;
}

const chaveDoEscopo = (e: { id: string | null; equipeId?: string | null }) =>
  e.equipeId ? `equipe:${e.equipeId}` : (e.id ?? PESSOAL);

function rotuloDoEscopo(e: Escopo): string {
  if (e.equipeId) return `Só a equipe ${e.nome}`;
  return e.id ? `Todo o escritório (${e.nome})` : e.nome;
}

function EscopoSelect({ escopos, value, onChange }: { escopos: Escopo[]; value: Escopo; onChange: (escopo: Escopo) => void }) {
  return (
    <div className="space-y-1">
      <Label htmlFor="tag-escopo">Quem vê</Label>
      <Select
        value={chaveDoEscopo(value)}
        onValueChange={(v) => onChange(escopos.find((e) => chaveDoEscopo(e) === v) ?? escopos[0])}
      >
        <SelectTrigger id="tag-escopo" className="h-11 sm:h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {escopos.map((e) => (
            <SelectItem key={chaveDoEscopo(e)} value={chaveDoEscopo(e)}>
              {rotuloDoEscopo(e)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Criar ou editar tag: nome, cor da paleta, escopo (só ao criar) e categoria do mesmo escopo. */
export function TagForm({
  inicial,
  escritorioId: escritorioInicial,
  equipeId: equipeInicial = null,
  categoriaId: categoriaInicial,
  escopos,
  categorias,
  salvando,
  onSalvar,
  onCancelar,
}: TagFormProps) {
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [cor, setCor] = useState(inicial?.cor ?? CORES_TAG[5].hex);
  const [escritorioId, setEscritorioId] = useState<string | null>(escritorioInicial);
  const [equipeId, setEquipeId] = useState<string | null>(equipeInicial);
  const [categoriaId, setCategoriaId] = useState<string | null>(categoriaInicial);

  // A categoria precisa ser do mesmo escopo da tag (D2); a da equipe usa as do escritório.
  const categoriasDoEscopo = categorias.filter((c) =>
    escritorioId ? c.escritorio_id === escritorioId : !c.escritorio_id,
  );
  const nomeLimpo = nome.trim();

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!nomeLimpo) return;
    try {
      await onSalvar({ nome: nomeLimpo, cor, categoriaId, escritorioId, equipeId });
    } catch {
      // O erro já foi mostrado; o formulário fica aberto para corrigir.
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <h3 className="text-sm font-semibold">{inicial ? 'Editar tag' : 'Nova tag'}</h3>
      <div className="space-y-1">
        <Label htmlFor="tag-nome">Nome</Label>
        <Input
          id="tag-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          maxLength={NOME_MAX}
          autoFocus
          className="h-11 sm:h-9"
          placeholder="Ex.: Hab. Herdeiros"
        />
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Cor</legend>
        <div className="flex flex-wrap gap-2">
          {CORES_TAG.map((opcao) => (
            <Button
              key={opcao.hex}
              type="button"
              variant="ghost"
              size="icon"
              role="radio"
              aria-checked={cor === opcao.hex}
              aria-label={opcao.nome}
              title={opcao.nome}
              onClick={() => setCor(opcao.hex)}
              className={cn(
                'h-11 w-11 rounded-full border border-black/15 p-0 hover:opacity-90 sm:h-9 sm:w-9',
                cor === opcao.hex && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
              )}
              style={{ backgroundColor: opcao.hex, color: corDoTexto(opcao.hex) }}
            >
              {cor === opcao.hex && <Check className="h-4 w-4" />}
            </Button>
          ))}
        </div>
      </fieldset>

      {!inicial && escopos.length > 1 && (
        <EscopoSelect
          escopos={escopos}
          value={{ id: escritorioId, nome: '', equipeId }}
          onChange={(escopo) => {
            setEscritorioId(escopo.id);
            setEquipeId(escopo.equipeId ?? null);
            setCategoriaId(null);
          }}
        />
      )}

      <div className="space-y-1">
        <Label htmlFor="tag-categoria">Categoria</Label>
        <Select
          value={categoriaId ?? SEM_CATEGORIA}
          onValueChange={(v) => setCategoriaId(v === SEM_CATEGORIA ? null : v)}
        >
          <SelectTrigger id="tag-categoria" className="h-11 sm:h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SEM_CATEGORIA}>Sem categoria</SelectItem>
            {categoriasDoEscopo.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {nomeLimpo && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          Prévia: <TagChip tag={{ nome: nomeLimpo, cor }} />
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={salvando} className="h-11 sm:h-9">
          Cancelar
        </Button>
        <Button type="submit" disabled={!nomeLimpo || salvando} className="h-11 sm:h-9">
          {salvando && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar
        </Button>
      </div>
    </form>
  );
}

interface CategoriaFormProps {
  inicial?: TagCategoria;
  escritorioId: string | null;
  escopos: Escopo[];
  salvando: boolean;
  onSalvar: (nome: string, escritorioId: string | null) => Promise<void>;
  onCancelar: () => void;
}

/** Criar ou renomear categoria. O escopo só se escolhe ao criar. */
export function CategoriaForm({ inicial, escritorioId: escritorioInicial, escopos, salvando, onSalvar, onCancelar }: CategoriaFormProps) {
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [escritorioId, setEscritorioId] = useState<string | null>(escritorioInicial);
  const nomeLimpo = nome.trim();

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!nomeLimpo) return;
    try {
      await onSalvar(nomeLimpo, escritorioId);
    } catch {
      // O erro já foi mostrado; o formulário fica aberto para corrigir.
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <h3 className="text-sm font-semibold">{inicial ? 'Renomear categoria' : 'Nova categoria'}</h3>
      <div className="space-y-1">
        <Label htmlFor="categoria-nome">Nome</Label>
        <Input
          id="categoria-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          maxLength={NOME_MAX}
          autoFocus
          className="h-11 sm:h-9"
          placeholder="Ex.: Habilitação"
        />
      </div>
      {!inicial && escopos.length > 1 && (
        <EscopoSelect
          escopos={escopos}
          value={{ id: escritorioId, nome: '' }}
          onChange={(escopo) => setEscritorioId(escopo.id)}
        />
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={salvando} className="h-11 sm:h-9">
          Cancelar
        </Button>
        <Button type="submit" disabled={!nomeLimpo || salvando} className="h-11 sm:h-9">
          {salvando && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar
        </Button>
      </div>
    </form>
  );
}
